import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Field, Select, TextArea, Toggle, type SelectOption } from '@/nova/components';

import type { JsonObject, JsonPrimitive, JsonValue, PlayableSchema } from '../../playable-api';
import { isColorSchema } from './schema-fields';
import * as S from './PlayableComposer.styles';

/**
 * The template settings form, generated from the template's JSON schema (the draft-07 subset the templates use):
 * strings (plain, `uri`, `textarea`, or a colour when the schema says `color` or restricts it to hex), numbers and
 * integers (within `minimum` / `maximum`), booleans, enums, nested objects, and arrays of primitives or objects of
 * up to 12 items. An absent key means the template's default, shown as the placeholder. Titles and descriptions
 * are the schema's own English text (backend data, shown as is).
 */

export interface SchemaFormProps {
  schema: PlayableSchema;
  value: JsonObject;
  onChange: (next: JsonObject) => void;
  disabled?: boolean;
}

/** The largest array the form edits item by item. */
const MAX_FORM_ITEMS = 12;

const isObject = (v: JsonValue | undefined): v is JsonObject =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/** A key's label: the schema title, else the key made readable (`brush_px` -> "Brush px"). Pure. */
const labelOf = (key: string, schema: PlayableSchema): string => {
  if (schema.title) return schema.title;
  const words = key.replace(/[_-]+/g, ' ').trim();
  return words ? words[0].toUpperCase() + words.slice(1) : key;
};


/** A hex colour as the native colour input takes it (`#rrggbb`), '' when it is not one. Pure. */
export const toHex6 = (value: string): string => {
  const v = value.trim();
  if (/^#[0-9a-f]{6}$/i.test(v)) return v.toLowerCase();
  const m = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i.exec(v);
  return m ? `#${m[1]}${m[1]}${m[2]}${m[2]}${m[3]}${m[3]}`.toLowerCase() : '';
};

/** An object with `key` set to `v`, or removed when `v` is undefined (back to the template default). Pure. */
const withKey = (obj: JsonObject, key: string, v: JsonValue | undefined): JsonObject => {
  const next: JsonObject = { ...obj };
  if (v === undefined) delete next[key];
  else next[key] = v;
  return next;
};

/** A new array item's value: the item schema's defaults; an integer with a range and no default steps through its
 *  range by position (six wheel segments get six different icons). Pure. */
const itemDefault = (schema: PlayableSchema | undefined, index: number): JsonValue => {
  if (!schema) return '';
  if (schema.default !== undefined) return schema.default;
  if (schema.enum && schema.enum.length > 0) return schema.enum[index % schema.enum.length];
  switch (schema.type) {
    case 'object': {
      const out: JsonObject = {};
      for (const [key, prop] of Object.entries(schema.properties ?? {})) {
        if (prop.type === 'string' && prop.default === undefined && !prop.enum) continue;
        out[key] = itemDefault(prop, index);
      }
      return out;
    }
    case 'integer':
    case 'number': {
      const min = schema.minimum ?? 0;
      if (schema.maximum === undefined) return min;
      const span = Math.max(1, Math.floor(schema.maximum - min) + 1);
      return min + (index % span);
    }
    case 'boolean':
      return false;
    case 'array':
      return [];
    default:
      return '';
  }
};

const primitiveText = (v: JsonValue | undefined): string =>
  v === undefined || v === null || typeof v === 'object' ? '' : String(v);

/* ---------- colour ---------- */

export interface ColorFieldProps {
  /** Translated (or schema) label. */
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

/** A colour: the native picker as a swatch beside the hex text (empty = the template's own colour). */
export const ColorField = ({ label, value, onChange, disabled = false }: ColorFieldProps): React.ReactElement => {
  const { t } = useTranslation();
  return (
    <S.ColorRow>
      <S.ColorInput
        value={toHex6(value)}
        aria-label={t('Pick {{name}}', { name: label })}
        disabled={disabled}
        onChange={(e) => {
          if (!disabled) onChange(e.target.value);
        }}
      />
      <Field label={label} value={value} onChange={onChange} placeholder="#RRGGBB" disabled={disabled} />
    </S.ColorRow>
  );
};

/* ---------- number ---------- */

interface NumberFieldProps {
  label: string;
  schema: PlayableSchema;
  value: JsonValue | undefined;
  onChange: (v: JsonValue | undefined) => void;
  disabled: boolean;
}

/** A number with its own typing buffer (a half-typed "0." or an out-of-range step is kept on screen, not saved). */
const NumberField = ({ label, schema, value, onChange, disabled }: NumberFieldProps): React.ReactElement => {
  const [text, setText] = React.useState(primitiveText(value));
  // The saved value changed from outside (a Lumio fill, a version switch): show it (the React "adjust state on a
  // prop change" pattern, no effect).
  const [seen, setSeen] = React.useState(value);
  if (value !== seen) {
    setSeen(value);
    if (Number(text) !== value) setText(primitiveText(value));
  }
  const min = schema.minimum;
  const max = schema.maximum;
  const parsed = text.trim() === '' ? undefined : Number(text);
  const invalid =
    parsed !== undefined &&
    (!Number.isFinite(parsed) ||
      (schema.type === 'integer' && !Number.isInteger(parsed)) ||
      (min !== undefined && parsed < min) ||
      (max !== undefined && parsed > max));

  const onText = (next: string): void => {
    if (disabled) return;
    setText(next);
    if (next.trim() === '') {
      onChange(undefined);
      return;
    }
    const n = Number(next);
    if (!Number.isFinite(n) || /[.-]$/.test(next.trim())) return;
    if (schema.type === 'integer' && !Number.isInteger(n)) return;
    if ((min !== undefined && n < min) || (max !== undefined && n > max)) return;
    onChange(n);
  };

  return (
    <S.FieldBlock>
      <Field
        label={label}
        value={text}
        onChange={onText}
        numeric
        placeholder={schema.default !== undefined ? primitiveText(schema.default) : undefined}
        disabled={disabled}
      />
      {invalid ? (
        min !== undefined && max !== undefined ? (
          <S.FieldError text="Use a number from {{min}} to {{max}}." placeholders={{ min, max }} />
        ) : (
          <S.FieldError text="Use a valid number." />
        )
      ) : null}
    </S.FieldBlock>
  );
};

/* ---------- one property ---------- */

interface SchemaFieldProps {
  name: string;
  schema: PlayableSchema;
  value: JsonValue | undefined;
  onChange: (v: JsonValue | undefined) => void;
  disabled: boolean;
  /** Nested inside an array item (no description line, to keep the row compact). */
  compact?: boolean;
}

const SchemaField = ({
  name,
  schema,
  value,
  onChange,
  disabled,
  compact = false,
}: SchemaFieldProps): React.ReactElement | null => {
  const { t } = useTranslation();
  const label = labelOf(name, schema);
  const description = !compact && schema.description ? <S.Hint raw text={schema.description} /> : null;

  if (schema.enum && schema.enum.length > 0) {
    const options: SelectOption[] = schema.enum.map((v) => ({ value: primitiveText(v), label: primitiveText(v) }));
    const current = value !== undefined ? value : schema.default ?? schema.enum[0];
    return (
      <S.FieldBlock>
        <Select
          label={label}
          value={primitiveText(current)}
          options={options}
          disabled={disabled}
          onChange={(text) => {
            if (disabled) return;
            const picked = (schema.enum ?? []).find((v: JsonPrimitive) => primitiveText(v) === text);
            if (picked !== undefined) onChange(picked);
          }}
        />
        {description}
      </S.FieldBlock>
    );
  }

  switch (schema.type) {
    case 'boolean': {
      const on = typeof value === 'boolean' ? value : schema.default === true;
      return (
        <S.FieldBlock>
          <S.ToggleRow>
            <S.ToggleLabel raw text={label} />
            <Toggle
              on={on}
              label={label}
              disabled={disabled}
              onToggle={() => {
                if (!disabled) onChange(!on);
              }}
            />
          </S.ToggleRow>
          {description}
        </S.FieldBlock>
      );
    }
    case 'integer':
    case 'number':
      return (
        <S.FieldBlock>
          <NumberField label={label} schema={schema} value={value} onChange={onChange} disabled={disabled} />
          {description}
        </S.FieldBlock>
      );
    case 'string': {
      const text = typeof value === 'string' ? value : '';
      const set = (v: string): void => {
        if (!disabled) onChange(v === '' ? undefined : v);
      };
      if (isColorSchema(schema)) {
        return (
          <S.FieldBlock>
            <ColorField label={label} value={text} onChange={set} disabled={disabled} />
            {description}
          </S.FieldBlock>
        );
      }
      if (schema.format === 'textarea') {
        return (
          <S.FieldBlock>
            <TextArea label={label} value={text} onChange={set} rows={3} disabled={disabled} />
            {description}
          </S.FieldBlock>
        );
      }
      return (
        <S.FieldBlock>
          <Field
            label={label}
            value={text}
            onChange={set}
            placeholder={
              schema.format === 'uri'
                ? t('https://… (optional)')
                : schema.default !== undefined
                  ? primitiveText(schema.default)
                  : undefined
            }
            disabled={disabled}
          />
          {description}
        </S.FieldBlock>
      );
    }
    case 'object': {
      const obj = isObject(value) ? value : {};
      return (
        <S.FieldBlock>
          <S.FieldLabel raw text={label} />
          {description}
          <SchemaForm
            schema={schema}
            value={obj}
            disabled={disabled}
            onChange={(next) => onChange(Object.keys(next).length > 0 ? next : undefined)}
          />
        </S.FieldBlock>
      );
    }
    case 'array':
      return (
        <ArrayField
          label={label}
          description={description}
          schema={schema}
          value={value}
          onChange={onChange}
          disabled={disabled}
        />
      );
    default:
      return null;
  }
};

/* ---------- arrays ---------- */

interface ArrayFieldProps {
  label: string;
  description: React.ReactNode;
  schema: PlayableSchema;
  value: JsonValue | undefined;
  onChange: (v: JsonValue | undefined) => void;
  disabled: boolean;
}

/** An array: the template default until customised, then one row per item (add up to `maxItems`, remove down to
 *  `minItems`, or reset to the default). */
const ArrayField = ({ label, description, schema, value, onChange, disabled }: ArrayFieldProps): React.ReactElement => {
  const { t } = useTranslation();
  const items = Array.isArray(value) ? value : undefined;
  const min = schema.minItems ?? 0;
  const max = Math.min(schema.maxItems ?? MAX_FORM_ITEMS, MAX_FORM_ITEMS);
  const itemSchema = schema.items;

  const customise = (): void => {
    if (disabled) return;
    const n = Math.max(min, 1);
    onChange(Array.from({ length: n }, (_, i) => itemDefault(itemSchema, i)));
  };
  const setItem = (index: number, v: JsonValue | undefined): void => {
    if (disabled || !items) return;
    onChange(items.map((item, i) => (i === index ? v ?? itemDefault(itemSchema, i) : item)));
  };
  const add = (): void => {
    if (disabled || !items || items.length >= max) return;
    onChange([...items, itemDefault(itemSchema, items.length)]);
  };
  const removeAt = (index: number): void => {
    if (disabled || !items || items.length <= min) return;
    onChange(items.filter((_, i) => i !== index));
  };

  return (
    <S.FieldBlock>
      <S.FieldLabel raw text={label} />
      {description}
      {items === undefined ? (
        <S.ArrayActions>
          <S.Hint text="Uses the template's default." />
          <Button variant="outline" label={t('Customise')} onClick={customise} disabled={disabled} />
        </S.ArrayActions>
      ) : (
        <S.ArrayList>
          {items.map((item, index) => (
            // Items have no identity of their own (a position in a fixed-order list), so the index is the key.
            <S.ArrayItem key={index}>
              <S.ArrayIndex raw text={`${index + 1}.`} />
              <S.ArrayItemBody>
                {itemSchema?.type === 'object' ? (
                  <SchemaForm
                    schema={itemSchema}
                    value={isObject(item) ? item : {}}
                    disabled={disabled}
                    compact
                    onChange={(next) => setItem(index, next)}
                  />
                ) : itemSchema ? (
                  <SchemaField
                    name={`${index + 1}`}
                    schema={itemSchema}
                    value={item}
                    disabled={disabled}
                    compact
                    onChange={(v) => setItem(index, v)}
                  />
                ) : null}
              </S.ArrayItemBody>
              <Button
                variant="text"
                icon="trash"
                iconOnly
                label={t('Remove')}
                onClick={() => removeAt(index)}
                disabled={disabled || items.length <= min}
              />
            </S.ArrayItem>
          ))}
          <S.ArrayActions>
            <Button
              variant="outline"
              icon="plus-circle"
              label={t('Add')}
              onClick={add}
              disabled={disabled || items.length >= max}
            />
            <Button
              variant="text"
              label={t('Reset to default')}
              onClick={() => onChange(undefined)}
              disabled={disabled}
            />
          </S.ArrayActions>
          {min > 0 || schema.maxItems !== undefined ? (
            <S.Hint text="From {{min}} to {{max}} items." placeholders={{ min, max }} />
          ) : null}
        </S.ArrayList>
      )}
    </S.FieldBlock>
  );
};

/* ---------- the form ---------- */

/** The settings form of an object schema: one field per property, in the schema's order. */
export const SchemaForm = ({
  schema,
  value,
  onChange,
  disabled = false,
  compact = false,
}: SchemaFormProps & { compact?: boolean }): React.ReactElement => {
  const entries = Object.entries(schema.properties ?? {});
  if (entries.length === 0) return <S.Hint text="This game has no extra settings." />;
  return (
    <S.FormStack>
      {entries.map(([key, prop]) => (
        <SchemaField
          key={key}
          name={key}
          schema={prop}
          value={value[key]}
          disabled={disabled}
          compact={compact}
          onChange={(v) => onChange(withKey(value, key, v))}
        />
      ))}
    </S.FormStack>
  );
};

export default SchemaForm;
